import type { MaskOptions, Track } from '@react-input/mask';

const NON_DIGIT = /\D/g;
const LEADING_US_COUNTRY_CODE = /^1(?=\d{10,}$)/;
const INVALID_LEADING_AREA_CODE = /^[01]/;

// Remove every non-digit character from a string.
export const trimPhone = (value?: string | null): string =>
  value ? value.replace(NON_DIGIT, '') : '';

export const formatPhoneNumberDisplay = (input: string): string => {
  const digits = trimPhone(input).slice(-10);
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

// Format a phone number into the same shape that `PHONE_MASK_OPTIONS` renders
export const formatPhoneNumberMasked = (input: string): string => {
  const digits = trimPhone(input);
  if (digits.length !== 10) return digits;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Normalizes phone number to E.164 format with +1 country code: +11234567890
export const formatPhoneNumberE164 = (phoneNumber: string) => {
  const result = phoneNumber.replace(NON_DIGIT, '');
  return result.startsWith('+1') ? result : `+1${result}`;
};

// Normalize phone input as the user types or pastes:
//   - keep only digits,
//   - drop a leading US country code (1) from 11+ digit pastes
//   - drop an invalid leading area code digit (0 or 1).
const sanitizePhoneInput: Track = ({ inputType, data, selectionStart }) => {
  const input = data ?? '';
  const isLeadingInsert =
    inputType.startsWith('insert') && (selectionStart ?? 0) <= 1;

  if (!isLeadingInsert) {
    return input;
  }

  return input
    .replace(NON_DIGIT, '')
    .replace(LEADING_US_COUNTRY_CODE, '')
    .replace(INVALID_LEADING_AREA_CODE, '');
};

// Mask preset for US phone numbers rendered as `(555) 123-4567`.
// Pass to `TextField` / `RhfTextField`'s `mask` prop.
export const PHONE_MASK_OPTIONS: MaskOptions = {
  mask: '(___) ___-____',
  replacement: { _: /\d/ },
  track: sanitizePhoneInput,
};
